import {
  FC,
  useState,
  useEffect,
  Fragment,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';

import { Step1 } from './Steps/Step1';
import { Step2 } from './Steps/Step2';
import { Step3 } from './Steps/Step3';
import { Step4 } from './Steps/Step4';
import { CompletionScript } from '../Completion/CompletionScript';
import { CompletionGame } from '../CompletionGame';
import type { IProduct } from '@/types/product';
import type { IGame } from '@/types/game';

interface GameProps {
  step: number;
  setStep: (value: number) => void;
  product: IProduct;
  setScenarios: Dispatch<React.SetStateAction<IGame[]>>;
  scenarios: IGame[];
  setIdGame: Dispatch<SetStateAction<number>>;
  idGame: number;
  configId: string;
  userToken: string;
  costPerQuestion: number;
  totalPoints: number;
}

const Game: FC<GameProps> = ({
  step,
  setStep,
  product,
  scenarios,
  setIdGame,
  idGame,
  setScenarios,
  configId,
  userToken,
  costPerQuestion,
  totalPoints,
}) => {
  const [isScore, setIsScore] = useState(false);
  const [game, setGame] = useState<IGame>();
  const [numberQuestion, setNumberQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string[]>([]);
  const [points, setPoints] = useState<number>(0);
  const [responseStatus, setResponseStatus] = useState<{
    [x: number]: boolean | null;
  }>({});
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState<number>(0);

  const isCompletedGameRef = useRef<boolean>();
  useEffect(() => {
    if (scenarios.find(({ id }) => id === idGame)) {
      isCompletedGameRef.current = scenarios.find(
        ({ id }) => id === idGame,
      )?.status;
    }
  }, [scenarios, idGame]);

  useEffect(() => {
    if (idGame) {
      fetch(
        `https://admin.informer.jti-games.dev.familyagency.ru/api/games/${idGame}`,
      )
        .then((response) => response.json())
        .then(({ data }) => setGame(data));
    }
  }, [idGame]);

  const allPoints = scenarios.reduce((acc, current) => acc + current.score, 0);

  return (
    <>
      {step === 9 && (
        <Step1
          setStep={setStep}
          setIdGame={setIdGame}
          setNumberQuestion={setNumberQuestion}
          numberQuestions={game?.questions.length || 0}
          idGame={idGame}
          scenarios={scenarios}
          currentScenarioIndex={currentScenarioIndex}
          setCurrentScenarioIndex={setCurrentScenarioIndex}
          costPerQuestion={costPerQuestion}
        />
      )}
      {game?.questions &&
        game.questions.map((item, index) => {
          const rightAnswers = item.answers.filter(({ isRight }) => isRight);

          const arrRightAnswer = rightAnswers
            .map(({ text }: { text: string }) => text)
            .sort();

          const trimSpaces = (str: string) => str.replace(/\s+/g, ' ').trim();

          const isRightAnswer =
            trimSpaces(JSON.stringify(arrRightAnswer)) ===
            trimSpaces(JSON.stringify(selectedAnswer.sort()));

          return (
            <Fragment key={item.id}>
              {step === 10 && item.text && numberQuestion === index + 1 && (
                <Step2
                  setStep={setStep}
                  question={item.text}
                  numberQuestion={numberQuestion}
                  questions={game.questions}
                  idGame={idGame}
                  setSelectedAnswer={setSelectedAnswer}
                  scenarios={scenarios}
                  points={points}
                  responseStatus={responseStatus}
                />
              )}
              {step === 11 && item && numberQuestion === index + 1 && (
                <Step3
                  setStep={setStep}
                  question={item}
                  selectedAnswer={selectedAnswer}
                  setSelectedAnswer={setSelectedAnswer}
                  numberQuestion={numberQuestion}
                  questions={game.questions}
                  idGame={idGame}
                  scenarios={scenarios}
                  points={points}
                  isRightAnswer={isRightAnswer}
                  setPoints={setPoints}
                  setResponseStatus={setResponseStatus}
                  responseStatus={responseStatus}
                  costPerQuestion={costPerQuestion}
                  userToken={userToken}
                />
              )}
              {step === 12 && numberQuestion === index + 1 && (
                <Step4
                  setIsScore={setIsScore}
                  setStep={setStep}
                  setNumberQuestion={setNumberQuestion}
                  selectedAnswer={selectedAnswer}
                  answers={item.answers}
                  numberQuestion={numberQuestion}
                  questions={game.questions}
                  idGame={idGame}
                  scenarios={scenarios}
                  isRightAnswer={isRightAnswer}
                  responseStatus={responseStatus}
                  setScenarios={setScenarios}
                  userToken={userToken}
                  configId={configId}
                  product={product}
                  costPerQuestion={costPerQuestion}
                  receivedPoints={points}
                  setResponseStatus={setResponseStatus}
                  game={game}
                  totalPoints={totalPoints}
                />
              )}
              {step === 13 && numberQuestion === index + 1 && (
                <CompletionScript
                  isSuccess={isRightAnswer}
                  numberQuestions={game.questions.length}
                  receivedPoints={points}
                  setStep={setStep}
                  scenarios={scenarios}
                  idGame={idGame}
                  setPoints={setPoints}
                  setIdGame={setIdGame}
                  isCompletedGameRef={isCompletedGameRef}
                  setCurrentScenarioIndex={setCurrentScenarioIndex}
                  costPerQuestion={costPerQuestion}
                />
              )}
            </Fragment>
          );
        })}
      {step === 14 && (
        <CompletionGame
          isShowPoints={isScore}
          allPoints={allPoints}
          setStep={setStep}
        />
      )}
    </>
  );
};

export default Game;
